import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../module_roots/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faEdit,
    faTrash,
    faEye,
    faSearch,
    faRefresh,
    faTruck,
    faSpinner,
    faExclamationTriangle,
    faCheckCircle,
    faTimes,
    faChevronLeft,
    faChevronRight,
    faFilter,
    faCalendarAlt,
    faFilePdf,
    faPrint,
    faDownload,
    faCalendar,
    faTimesCircle,
    faUser,
    faPhone,
    faEnvelope,
    faMapMarkerAlt,
    faBuilding,
    faGlobe,
    faUserTag,
    faStickyNote,
    faIdCard,
    faSave,
    faBan,
    faUserEdit,
    faImage,
    faHashtag,
    faUserPlus,
    faExternalLinkAlt,
    faLink,
    faStore,
    faExchangeAlt,
    faRulerCombined,
    faLayerGroup,
    faExpand,
    faCompress,
    faArrowsAlt,
    faMinus,
    faPlus
} from '@fortawesome/free-solid-svg-icons';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import "../styles/CustomerList.css";
import "../styles/Customer.css";

// Create translator instance with the SAME pattern as customerlist.js
const createSupplierListTranslator = () => {
    let cache = new Map();
    let preloadedLanguages = new Set(['en', 'ur', 'ar', 'es']);

    const loadFromCache = () => {
        try {
            const cached = localStorage.getItem('ourzest_translations');
            if (cached) {
                const parsed = JSON.parse(cached);
                Object.entries(parsed).forEach(([key, value]) => {
                    cache.set(key, value);
                });
            }
        } catch (e) {
            console.log("No cache found in Supplier List");
        }
    };

    const saveToCache = () => {
        try {
            const cacheObj = {};
            cache.forEach((value, key) => {
                cacheObj[key] = value;
            });
            localStorage.setItem('ourzest_translations', JSON.stringify(cacheObj));
        } catch (e) {
            console.error("Error saving to cache:", e);
        }
    };

    const translateAPI = async (text, targetLang) => {
        if (targetLang === 'en') return text;

        try {
            const response = await fetch('https://translate.argosopentech.com/translate', {
                method: 'POST',
                body: JSON.stringify({ q: text, source: 'en', target: targetLang }),
                headers: { 'Content-Type': 'application/json' }
            });

            if (response.ok) {
                const data = await response.json();
                return data.translatedText;
            }
        } catch (e) {
            console.log("LibreTranslate failed, trying MyMemory");
        }

        try {
            const response = await fetch(
                `https://api.mymemory.translated.net/get?q=${encodeURIComponent(text)}&langpair=en|${targetLang}`
            );
            if (response.ok) {
                const data = await response.json();
                return data.responseData.translatedText;
            }
        } catch (e) {
            console.log("MyMemory translation failed");
        }

        return text;
    };

    const translateText = async (text, targetLang) => {
        const cacheKey = `${text}_${targetLang}`;

        if (cache.has(cacheKey)) {
            return cache.get(cacheKey);
        }

        const translated = await translateAPI(text, targetLang);
        cache.set(cacheKey, translated);
        saveToCache();

        return translated;
    };

    const defaultTexts = {
        supplierList: 'Supplier List',
        allSuppliers: 'Shop Suppliers',
        shopSuppliers: 'Shop Suppliers',
        shopName: 'Shop',
        shopCode: 'Shop Code',
        currentShop: 'Current Shop',
        searchPlaceholder: 'Search supplier in shop',
        refresh: 'Refresh',
        loadingSuppliers: 'Loading shop suppliers...',
        noSuppliersFound: 'No suppliers found in shop',
        supplierDetails: 'Supplier Details',
        close: 'Close',
        serialNo: 'S.No',
        id: 'ID',
        supplierName: 'Supplier Name',
        phone: 'Phone',
        email: 'Email',
        company: 'Company',
        address: 'Address',
        website: 'Website',
        contactPerson: 'Contact Person',
        notes: 'Notes',
        actions: 'Actions',
        view: 'View',
        edit: 'Edit',
        delete: 'Delete',
        deleteConfirm: 'Are you sure you want to delete this supplier from your shop?',
        deletedSuccess: 'Supplier deleted successfully',
        deleteError: 'Error deleting supplier',
        networkError: 'Network error occurred',
        noShopPermission: 'You are not assigned to any shop. Please contact administrator.',
        shopPermissionError: 'You cannot edit/delete suppliers added by other shop members',
        supplierId: 'Supplier ID',
        name: 'Name',
        notProvided: 'Not provided',
        showing: 'Showing',
        of: 'of',
        suppliers: 'suppliers',
        previous: 'Previous',
        next: 'Next',
        filter: 'Filter',
        all: 'All',
        withPhone: 'With Phone',
        withoutPhone: 'Without Phone',
        withEmail: 'With Email',
        withoutEmail: 'Without Email',
        filterBy: 'Filter By',
        selectFilter: 'Select Filter',
        phoneNumber: 'Phone Number',
        emailAddress: 'Email Address',
        companyName: 'Company Name',
        supplierIdFilter: 'Supplier ID',
        dateRange: 'Date Range',
        startDate: 'Start Date',
        endDate: 'End Date',
        applyFilters: 'Apply Filters',
        resetFilters: 'Reset Filters',
        exportToPDF: 'PDF',
        printList: 'Print List',
        exportData: 'Export Data',
        filterResults: 'Filter Results',
        clearAll: 'Clear All',
        editSupplier: 'Edit Supplier',
        updateSupplier: 'Update Supplier',
        updating: 'Updating...',
        supplierUpdated: 'Supplier updated successfully',
        updateError: 'Error updating supplier',
        cancel: 'Cancel',
        saveChanges: 'Save Changes',
        fillDetails: 'Fill in the supplier details below',
        nameRequired: 'Supplier name is required',
        nameTooShort: 'Name must be at least 2 characters',
        phoneInvalid: 'Please enter a valid phone number',
        emailInvalid: 'Please enter a valid email address',
        websiteInvalid: 'Please enter a valid website URL',
        confirmDelete: 'Confirm Delete',
        deleteMessage: 'Are you sure you want to delete this supplier from your shop?',
        deleteWarning: 'This action cannot be undone.',
        yesDelete: 'Yes, Delete',
        noCancel: 'No, Cancel',
        myRecords: 'All Shop Records',
        loadingUserData: 'Loading shop supplier data...',
        noUserRecords: 'No suppliers added to your shop yet',
        addFirstSupplier: 'Add First Supplier to Shop',
        businessReport: 'Shop Report',
        generatedBy: 'Generated by',
        supplierReport: 'Supplier Report',
        success: 'Success',
        error: 'Error',
        warning: 'Warning',
        allShopMembers: 'All Shop Members',
        teamSuppliers: 'Team Suppliers',
        shopDashboard: 'Shop Dashboard',
        switchShop: 'Switch Shop',
        loadingShops: 'Loading shops...',
        noShops: 'No shops available',
        current: 'Current',
        noShopSelected: 'No shop selected. Please switch to a shop first.',
        selectShop: 'Please select a shop to view suppliers',
        shopSwitched: 'Shop switched successfully',
        shopSwitchFailed: 'Failed to switch shop',
        optional: 'Optional',
        orientation: 'Orientation',
        portrait: 'Portrait',
        landscape: 'Landscape',
        pageSize: 'Page Size',
        a4: 'A4',
        letter: 'Letter',
        legal: 'Legal',
        a5: 'A5'
    };

    const getTranslations = async (lang) => {
        if (lang === 'en') {
            return defaultTexts;
        }

        const translations = {};
        const promises = [];

        Object.keys(defaultTexts).forEach(key => {
            const cacheKey = `${defaultTexts[key]}_${lang}`;
            if (cache.has(cacheKey)) {
                translations[key] = cache.get(cacheKey);
            } else {
                promises.push(
                    translateText(defaultTexts[key], lang).then(translated => {
                        translations[key] = translated;
                    })
                );
            }
        });

        if (promises.length > 0) {
            await Promise.all(promises);
            saveToCache();
        }

        return translations;
    };

    loadFromCache();

    return {
        getTranslations,
        translateText
    };
};

const supplierListTranslator = createSupplierListTranslator();

// Toast Notification Component
const ToastNotification = ({ toast, setToast, translations }) => {
    const getIconForType = (type) => {
        switch (type) {
            case 'success':
                return faCheckCircle;
            case 'error':
                return faExclamationTriangle;
            case 'warning':
                return faExclamationTriangle;
            default:
                return faCheckCircle;
        }
    };

    const getTitleForType = (type) => {
        switch (type) {
            case 'success':
                return translations.success || 'Success';
            case 'error':
                return translations.error || 'Error';
            case 'warning':
                return translations.warning || 'Warning';
            default:
                return '';
        }
    };

    if (!toast.show) return null;

    const opacity = Math.max(toast.progress / 100, 0.1);

    return (
        <div
            className={`toast-notification ${toast.type} ${toast.direction === 'rtl' ? 'toast-rtl' : 'toast-ltr'}`}
            style={{ opacity }}
        >
            <div className="toast-content">
                <div className="toast-icon">
                    <FontAwesomeIcon icon={getIconForType(toast.type)} />
                </div>
                <div className="toast-text">
                    <div className="toast-title">
                        {getTitleForType(toast.type)}
                    </div>
                    <div className="toast-message">
                        {toast.message}
                    </div>
                </div>
                <button
                    className="toast-close"
                    onClick={() => setToast(prev => ({ ...prev, show: false }))}
                >
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>
            <div className="toast-timer">
                <div
                    className="toast-progress-bar"
                    style={{
                        width: `${toast.progress}%`,
                        transition: 'width 0.1s linear'
                    }}
                ></div>
                <div className="toast-countdown">
                    {toast.timeLeft}s
                </div>
            </div>
        </div>
    );
};

// Shop Selector Modal Component
const ShopSelectorModal = ({ isOpen, onClose, shops, currentShop, onSelectShop, translations, loading }) => {
    if (!isOpen) return null;

    const handleShopSelect = (shop) => {
        onSelectShop(shop);
        onClose();
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content shop-selector-modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h3>{translations.switchShop || 'Switch Shop'}</h3>
                    <button className="modal-close" onClick={onClose}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>
                <div className="modal-body">
                    {loading ? (
                        <div className="loading-shops">
                            <div className="loading-spinner-small"></div>
                            <p>{translations.loadingShops || 'Loading shops...'}</p>
                        </div>
                    ) : shops.length === 0 ? (
                        <div className="no-shops">
                            <p>{translations.noShops || 'No shops available'}</p>
                        </div>
                    ) : (
                        <div className="shop-list">
                            {shops.map((shop) => (
                                <div
                                    key={shop.shop_id}
                                    className={`shop-item ${currentShop?.shop_id === shop.shop_id ? 'active' : ''}`}
                                    onClick={() => handleShopSelect(shop)}
                                >
                                    <div className="shop-item-icon">
                                        <FontAwesomeIcon icon={faStore} />
                                    </div>
                                    <div className="shop-item-details">
                                        <div className="shop-item-name">{shop.shop_name}</div>
                                        <div className="shop-item-code">{shop.shop_code || `ID: ${shop.shop_id}`}</div>
                                        {shop.shop_role && (
                                            <div className="shop-item-role">Role: {shop.shop_role}</div>
                                        )}
                                    </div>
                                    {currentShop?.shop_id === shop.shop_id && (
                                        <div className="shop-item-current">
                                            <FontAwesomeIcon icon={faCheckCircle} />
                                            <span>{translations.current || 'Current'}</span>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// Width modes for modal
const widthModes = {
    narrow: { width: 400, label: 'Narrow', icon: faMinus },
    medium: { width: 650, label: 'Medium', icon: faArrowsAlt },
    wide: { width: 850, label: 'Wide', icon: faPlus },
    full: { width: '95vw', label: 'Full Width', icon: faExpand },
    fullscreen: { width: '98vw', label: 'Full Screen', icon: faCompress }
};

const SupplierList = () => {
    const { user, currentShop, switchShop, getShopsForSwitch } = useAuth();
    const [suppliers, setSuppliers] = useState([]);
    const [filteredSuppliers, setFilteredSuppliers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pdfLoading, setPdfLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedSupplier, setSelectedSupplier] = useState(null);
    const [showViewModal, setShowViewModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [supplierToDelete, setSupplierToDelete] = useState(null);
    const [editLoading, setEditLoading] = useState(false);

    // Modal drag and resize state
    const [modalPosition, setModalPosition] = useState({ x: 0, y: 0 });
    const [modalSize, setModalSize] = useState({ width: 650, height: 'auto' });
    const [isDragging, setIsDragging] = useState(false);
    const [isResizing, setIsResizing] = useState(false);
    const [currentWidthMode, setCurrentWidthMode] = useState('medium');
    const [isModalInitialized, setIsModalInitialized] = useState(false);

    const dragStart = useRef({ x: 0, y: 0 });
    const modalRef = useRef(null);
    const resizeType = useRef(null);
    const resizeStart = useRef({ x: 0, y: 0, width: 0, height: 0, left: 0, top: 0 });

    // PDF Options State
    const [pdfOrientation, setPdfOrientation] = useState('portrait');
    const [pdfPageSize, setPdfPageSize] = useState('a4');
    const [pdfPageRange, setPdfPageRange] = useState('all');
    const [pdfStartPage, setPdfStartPage] = useState(1);
    const [pdfEndPage, setPdfEndPage] = useState(1);

    // Toast state
    const [toast, setToast] = useState({
        show: false,
        type: '',
        message: '',
        direction: 'ltr',
        progress: 100,
        timeLeft: 3
    });

    // Shop selector state
    const [showShopSelector, setShowShopSelector] = useState(false);
    const [availableShops, setAvailableShops] = useState([]);
    const [loadingShops, setLoadingShops] = useState(false);

    // Shop info state
    const [shopInfo, setShopInfo] = useState({
        shop_id: null,
        shop_name: '',
        shop_code: '',
        shop_users_count: 0
    });

    // Timer reference for cleanup
    const toastTimerRef = useRef(null);

    // Edit form state with validation errors
    const [editFormData, setEditFormData] = useState({
        supplier_name: '',
        phone: '',
        email: '',
        company: '',
        address: '',
        website: '',
        contact_person: '',
        notes: ''
    });
    const [editFormErrors, setEditFormErrors] = useState({});

    // Business info state
    const [businessInfo, setBusinessInfo] = useState({
        businessname: '',
        bussinesslogo: '',
        businessaddress: '',
        fullname: '',
        email: ''
    });
    const [businessInfoLoading, setBusinessInfoLoading] = useState(false);

    // Get theme and language from localStorage
    const [currentLanguage, setCurrentLanguage] = useState(() => {
        return localStorage.getItem('ourzest_language') || 'en';
    });
    const [currentTheme, setCurrentTheme] = useState(() => {
        return localStorage.getItem('ourzest_theme') || '#6366f1';
    });

    const [translations, setTranslations] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(20);

    // Enhanced filter states
    const [filterType, setFilterType] = useState('all');
    const [filterBy, setFilterBy] = useState('all');
    const [phoneFilter, setPhoneFilter] = useState('');
    const [emailFilter, setEmailFilter] = useState('');
    const [companyFilter, setCompanyFilter] = useState('');
    const [supplierIdFilter, setSupplierIdFilter] = useState('');

    const totalPages = Math.ceil(filteredSuppliers.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentSuppliers = filteredSuppliers.slice(startIndex, endIndex);

    // Center modal function
    const centerModal = useCallback(() => {
        if (!modalRef.current) return;
        
        const rect = modalRef.current.getBoundingClientRect();
        const left = (window.innerWidth - rect.width) / 2;
        const top = (window.innerHeight - rect.height) / 2;
        
        modalRef.current.style.left = `${Math.max(0, left)}px`;
        modalRef.current.style.top = `${Math.max(0, top)}px`;
        modalRef.current.style.transform = 'none';
        
        setModalPosition({ x: Math.max(0, left), y: Math.max(0, top) });
    }, []);

    // Set width mode
    const setWidthMode = useCallback((mode) => {
        if (!modalRef.current) return;
        
        setCurrentWidthMode(mode);
        const widthValue = widthModes[mode].width;
        
        if (mode === 'fullscreen') {
            modalRef.current.style.width = '98vw';
            modalRef.current.style.height = '90vh';
            modalRef.current.style.maxWidth = '98vw';
            modalRef.current.style.maxHeight = '90vh';
        } else {
            modalRef.current.style.width = typeof widthValue === 'number' ? `${widthValue}px` : widthValue;
            modalRef.current.style.height = 'auto';
            modalRef.current.style.maxWidth = '95vw';
            modalRef.current.style.maxHeight = '85vh';
        }
        
        setModalSize({ width: modalRef.current.offsetWidth, height: modalRef.current.offsetHeight });
        
        setTimeout(() => centerModal(), 10);
    }, [centerModal]);

    // Start dragging
    const startDrag = useCallback((e) => {
        if (!modalRef.current) return;
        
        if (e.target.closest('button, input, select, textarea, a, .width-btn, .close-btn-modern')) {
            return;
        }
        
        e.preventDefault();
        setIsDragging(true);
        
        const rect = modalRef.current.getBoundingClientRect();
        dragStart.current = {
            x: e.clientX - rect.left,
            y: e.clientY - rect.top
        };
        
        modalRef.current.style.transition = 'none';
        modalRef.current.classList.add('dragging');
    }, []);

    // Start resizing
    const startResize = useCallback((e, type) => {
        e.preventDefault();
        e.stopPropagation();
        
        if (!modalRef.current) return;
        
        setIsResizing(true);
        resizeType.current = type;
        
        const rect = modalRef.current.getBoundingClientRect();
        
        resizeStart.current = {
            x: e.clientX,
            y: e.clientY,
            width: rect.width,
            height: rect.height,
            left: rect.left,
            top: rect.top,
            minWidth: 350,
            minHeight: 450,
            maxWidth: window.innerWidth - 40,
            maxHeight: window.innerHeight - 40
        };
        
        modalRef.current.style.transition = 'none';
        modalRef.current.classList.add('resizing');
    }, []);

    // Handle drag and resize movement
    useEffect(() => {
        const handleMouseMove = (e) => {
            if (isDragging && modalRef.current) {
                let newLeft = e.clientX - dragStart.current.x;
                let newTop = e.clientY - dragStart.current.y;
                
                const rect = modalRef.current.getBoundingClientRect();
                newLeft = Math.max(0, Math.min(newLeft, window.innerWidth - rect.width));
                newTop = Math.max(0, Math.min(newTop, window.innerHeight - rect.height));
                
                modalRef.current.style.left = `${newLeft}px`;
                modalRef.current.style.top = `${newTop}px`;
                modalRef.current.style.transform = 'none';
                
                setModalPosition({ x: newLeft, y: newTop });
            }
            
            if (isResizing && modalRef.current && resizeType.current) {
                let newWidth = resizeStart.current.width;
                let newHeight = resizeStart.current.height;
                let newLeft = resizeStart.current.left;
                let newTop = resizeStart.current.top;
                
                const deltaX = e.clientX - resizeStart.current.x;
                const deltaY = e.clientY - resizeStart.current.y;
                
                switch (resizeType.current) {
                    case 'e':
                        newWidth = Math.min(resizeStart.current.maxWidth, Math.max(resizeStart.current.minWidth, resizeStart.current.width + deltaX));
                        break;
                    case 'w':
                        newWidth = Math.min(resizeStart.current.maxWidth, Math.max(resizeStart.current.minWidth, resizeStart.current.width - deltaX));
                        newLeft = resizeStart.current.left + (resizeStart.current.width - newWidth);
                        break;
                    case 's':
                        newHeight = Math.min(resizeStart.current.maxHeight, Math.max(resizeStart.current.minHeight, resizeStart.current.height + deltaY));
                        break;
                    case 'n':
                        newHeight = Math.min(resizeStart.current.maxHeight, Math.max(resizeStart.current.minHeight, resizeStart.current.height - deltaY));
                        newTop = resizeStart.current.top + (resizeStart.current.height - newHeight);
                        break;
                    case 'ne':
                        newWidth = Math.min(resizeStart.current.maxWidth, Math.max(resizeStart.current.minWidth, resizeStart.current.width + deltaX));
                        newHeight = Math.min(resizeStart.current.maxHeight, Math.max(resizeStart.current.minHeight, resizeStart.current.height - deltaY));
                        newTop = resizeStart.current.top + (resizeStart.current.height - newHeight);
                        break;
                    case 'nw':
                        newWidth = Math.min(resizeStart.current.maxWidth, Math.max(resizeStart.current.minWidth, resizeStart.current.width - deltaX));
                        newHeight = Math.min(resizeStart.current.maxHeight, Math.max(resizeStart.current.minHeight, resizeStart.current.height - deltaY));
                        newLeft = resizeStart.current.left + (resizeStart.current.width - newWidth);
                        newTop = resizeStart.current.top + (resizeStart.current.height - newHeight);
                        break;
                    case 'se':
                        newWidth = Math.min(resizeStart.current.maxWidth, Math.max(resizeStart.current.minWidth, resizeStart.current.width + deltaX));
                        newHeight = Math.min(resizeStart.current.maxHeight, Math.max(resizeStart.current.minHeight, resizeStart.current.height + deltaY));
                        break;
                    case 'sw':
                        newWidth = Math.min(resizeStart.current.maxWidth, Math.max(resizeStart.current.minWidth, resizeStart.current.width - deltaX));
                        newHeight = Math.min(resizeStart.current.maxHeight, Math.max(resizeStart.current.minHeight, resizeStart.current.height + deltaY));
                        newLeft = resizeStart.current.left + (resizeStart.current.width - newWidth);
                        break;
                }
                
                modalRef.current.style.width = `${newWidth}px`;
                modalRef.current.style.height = `${newHeight}px`;
                modalRef.current.style.left = `${newLeft}px`;
                modalRef.current.style.top = `${newTop}px`;
                modalRef.current.style.transform = 'none';
                
                setModalSize({ width: newWidth, height: newHeight });
                setModalPosition({ x: newLeft, y: newTop });
            }
        };
        
        const handleMouseUp = () => {
            if (isDragging || isResizing) {
                setIsDragging(false);
                setIsResizing(false);
                resizeType.current = null;
                
                if (modalRef.current) {
                    modalRef.current.style.transition = '';
                    modalRef.current.classList.remove('dragging', 'resizing');
                }
            }
        };
        
        if (isDragging || isResizing) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            document.body.classList.add('dragging-active');
        }
        
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            document.body.classList.remove('dragging-active');
        };
    }, [isDragging, isResizing]);

    // Initialize modal position when opened
    useEffect(() => {
        if (showEditModal && modalRef.current && !isModalInitialized) {
            setTimeout(() => {
                centerModal();
                setWidthMode('medium');
                setIsModalInitialized(true);
            }, 50);
        }
        
        if (!showEditModal) {
            setIsModalInitialized(false);
        }
    }, [showEditModal, centerModal, setWidthMode, isModalInitialized]);

    // Reset position on window resize
    useEffect(() => {
        const handleWindowResize = () => {
            if (!modalRef.current || !showEditModal) return;
            
            const rect = modalRef.current.getBoundingClientRect();
            let newLeft = Math.min(rect.left, window.innerWidth - rect.width);
            let newTop = Math.min(rect.top, window.innerHeight - rect.height);
            newLeft = Math.max(0, newLeft);
            newTop = Math.max(0, newTop);
            
            if (newLeft !== rect.left || newTop !== rect.top) {
                modalRef.current.style.left = `${newLeft}px`;
                modalRef.current.style.top = `${newTop}px`;
                setModalPosition({ x: newLeft, y: newTop });
            }
        };
        
        window.addEventListener('resize', handleWindowResize);
        return () => window.removeEventListener('resize', handleWindowResize);
    }, [showEditModal]);

    // Helper function to convert hex to rgba
    const hexToRgba = (hex, alpha = 1) => {
        const r = parseInt(hex.slice(1, 3), 16);
        const g = parseInt(hex.slice(3, 5), 16);
        const b = parseInt(hex.slice(5, 7), 16);
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    };

    // Calculate rows per page based on orientation and page size
    const getRowsPerPage = () => {
        if (pdfPageSize === 'a5') return pdfOrientation === 'portrait' ? 31 : 35;
        if (pdfPageSize === 'legal') return pdfOrientation === 'portrait' ? 35 : 38;
        if (pdfPageSize === 'letter') return pdfOrientation === 'portrait' ? 32 : 36;
        return pdfOrientation === 'portrait' ? 31 : 34;
    };

    // Function to show toast
    const showToast = (type, message) => {
        if (toastTimerRef.current) {
            clearInterval(toastTimerRef.current);
        }

        const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';
        const direction = isRTL ? 'rtl' : 'ltr';

        setToast({
            show: true,
            type,
            message,
            direction,
            progress: 100,
            timeLeft: 3
        });

        let timeLeft = 3;
        const timer = setInterval(() => {
            timeLeft -= 0.1;
            const progress = (timeLeft / 3) * 100;

            setToast(prev => ({
                ...prev,
                progress,
                timeLeft: parseFloat(timeLeft.toFixed(1))
            }));

            if (timeLeft <= 0) {
                clearInterval(timer);
                setToast(prev => ({ ...prev, show: false }));
            }
        }, 100);

        toastTimerRef.current = timer;
    };

    // Clean up timer on component unmount
    useEffect(() => {
        return () => {
            if (toastTimerRef.current) {
                clearInterval(toastTimerRef.current);
            }
        };
    }, []);

    // Apply theme from login page
    useEffect(() => {
        const applyTheme = (color) => {
            try {
                if (!/^#[0-9A-F]{6}$/i.test(color)) color = '#6366f1';

                const hex = color.replace('#', '');
                const r = parseInt(hex.substring(0, 2), 16);
                const g = parseInt(hex.substring(2, 4), 16);
                const b = parseInt(hex.substring(4, 6), 16);

                const adjust = (amount) => {
                    let newR = Math.max(0, Math.min(255, r + amount));
                    let newG = Math.max(0, Math.min(255, g + amount));
                    let newB = Math.max(0, Math.min(255, b + amount));
                    return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
                };

                const palette = {
                    'customer-primary-color': color,
                    'customer-primary-dark': adjust(-40),
                    'customer-primary-light': adjust(40),
                    'customer-primary-ultralight': adjust(20),
                    'customer-primary-gradient': `linear-gradient(135deg, ${color}, ${adjust(-20)})`,
                    'customer-primary-shadow': `${color}20`
                };

                Object.entries(palette).forEach(([key, value]) => {
                    document.documentElement.style.setProperty(`--${key}`, value);
                });

                document.documentElement.style.setProperty('--primary-color', color);
                document.documentElement.style.setProperty('--primary-dark', adjust(-40));
                document.documentElement.style.setProperty('--primary-light', adjust(40));
                document.documentElement.style.setProperty('--primary-ultralight', adjust(20));
                document.documentElement.style.setProperty('--primary-gradient', `linear-gradient(135deg, ${color}, ${adjust(-20)})`);
                document.documentElement.style.setProperty('--primary-shadow', `${color}20`);
                document.documentElement.style.setProperty('--row-active', adjust(10));

            } catch (err) {
                console.error("Error applying theme:", err);
            }
        };

        applyTheme(currentTheme);

        const handleThemeChange = () => {
            const theme = localStorage.getItem('ourzest_theme') || '#6366f1';
            if (theme !== currentTheme) {
                setCurrentTheme(theme);
                applyTheme(theme);
            }
        };

        handleThemeChange();

        window.addEventListener('storage', handleThemeChange);

        const interval = setInterval(handleThemeChange, 1000);

        return () => {
            window.removeEventListener('storage', handleThemeChange);
            clearInterval(interval);
        };
    }, [currentTheme]);

    // Load translations
    useEffect(() => {
        const loadTranslations = async () => {
            const trans = await supplierListTranslator.getTranslations(currentLanguage);
            setTranslations(trans);
        };
        loadTranslations();
    }, [currentLanguage]);

    // Listen for language changes
    useEffect(() => {
        const handleLanguageChange = () => {
            const lang = localStorage.getItem('ourzest_language') || 'en';
            if (lang !== currentLanguage) {
                setCurrentLanguage(lang);
            }
        };

        handleLanguageChange();

        window.addEventListener('storage', handleLanguageChange);

        const interval = setInterval(handleLanguageChange, 1000);

        return () => {
            window.removeEventListener('storage', handleLanguageChange);
            clearInterval(interval);
        };
    }, [currentLanguage]);

    // Apply RTL direction when language changes
    useEffect(() => {
        const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';

        if (isRTL) {
            document.documentElement.dir = 'rtl';
            document.body.dir = 'rtl';
            document.documentElement.style.direction = 'rtl';
            document.documentElement.lang = currentLanguage;
            const tables = document.querySelectorAll('.customer-list-modern, .table-container-modern, .customer-table-modern');
            tables.forEach(table => {
                table.style.direction = 'rtl';
                table.style.textAlign = 'right';
            });
        } else {
            document.documentElement.dir = 'ltr';
            document.body.dir = 'ltr';
            document.documentElement.style.direction = 'ltr';
            document.documentElement.lang = currentLanguage;
            const tables = document.querySelectorAll('.customer-list-modern, .table-container-modern, .customer-table-modern');
            tables.forEach(table => {
                table.style.direction = 'ltr';
                table.style.textAlign = 'left';
            });
        }

        return () => {
            document.documentElement.dir = 'ltr';
            document.body.dir = 'ltr';
            document.documentElement.style.direction = 'ltr';
        };
    }, [currentLanguage]);

    // Fetch suppliers on mount and when shop changes
    useEffect(() => {
        if (user && user.username) {
            fetchSuppliers();
            fetchAvailableShops();
            fetchBusinessInfo();
        } else {
            setLoading(false);
            showToast('error', 'User not authenticated. Please login again.');
        }
    }, [user, currentShop]);

    // Apply filters when suppliers, search term, or filter type changes
    useEffect(() => {
        applyFilters();
    }, [suppliers, searchTerm, filterType, filterBy, phoneFilter, emailFilter, companyFilter, supplierIdFilter]);

    // Fetch business info from root_users
    const fetchBusinessInfo = async () => {
        if (!user || !user.username) return;

        setBusinessInfoLoading(true);
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/get_business_info.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    action: 'get_business_info',
                    username: user.username
                })
            });

            const data = await response.json();
            if (data.status === 'success') {
                setBusinessInfo({
                    businessname: data.businessname || '',
                    bussinesslogo: data.bussinesslogo || '',
                    businessaddress: data.businessaddress || '',
                    fullname: data.fullname || '',
                    email: data.email || ''
                });
            } else {
                console.log('Could not fetch business info:', data.message);
                setBusinessInfo({
                    businessname: user.business_name || '',
                    bussinesslogo: '',
                    businessaddress: '',
                    fullname: user.fullname || '',
                    email: user.email || ''
                });
            }
        } catch (error) {
            console.error('Error fetching business info:', error);
            setBusinessInfo({
                businessname: user.business_name || '',
                bussinesslogo: '',
                businessaddress: '',
                fullname: user.fullname || '',
                email: user.email || ''
            });
        } finally {
            setBusinessInfoLoading(false);
        }
    };

    // Fetch available shops for switching
    const fetchAvailableShops = async () => {
        if (!user) return;

        setLoadingShops(true);
        try {
            const shopsData = await getShopsForSwitch();
            setAvailableShops(shopsData.shops || []);
        } catch (error) {
            console.error('Error loading shops:', error);
            setAvailableShops([]);
        } finally {
            setLoadingShops(false);
        }
    };

    const handleSwitchShop = async (shop) => {
        try {
            const success = await switchShop(
                shop.shop_id,
                shop.shop_name,
                shop.shop_code
            );

            if (success) {
                showToast('success', translations.shopSwitched || 'Shop switched successfully');
                fetchSuppliers();
            } else {
                showToast('error', translations.shopSwitchFailed || 'Failed to switch shop');
            }
        } catch (error) {
            console.error('Error switching shop:', error);
            showToast('error', translations.shopSwitchFailed || 'Failed to switch shop');
        }
    };

    // Render shop indicator
    const renderShopIndicator = () => {
        if (!currentShop) {
            return (
                <div className="shop-indicator-modern no-shop">
                    <div className="shop-badge">
                        <FontAwesomeIcon icon={faExclamationTriangle} />
                        <span className="shop-warning">
                            {translations.noShopSelected || 'No shop selected'}
                        </span>
                    </div>
                    <button
                        className="btn-modern btn-switch-shop"
                        onClick={() => setShowShopSelector(true)}
                    >
                        <FontAwesomeIcon icon={faStore} />
                        <span>{translations.switchShop || 'Switch Shop'}</span>
                    </button>
                </div>
            );
        }
        return null;
    };

    const fetchSuppliers = async () => {
        if (!user || !user.username) {
            showToast('error', 'User information not available. Please login again.');
            setLoading(false);
            return;
        }

        if (!currentShop) {
            showToast('error', translations.noShopSelected || 'Please select a shop first');
            setLoading(false);
            return;
        }

        setLoading(true);

        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/suppliers.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    action: 'get_records',
                    username: user.username,
                    shop_id: currentShop.shop_id
                })
            });

            const data = await response.json();

            if (data.status === 'success') {
                setSuppliers(data.data || []);

                if (data.shop_id) {
                    setShopInfo(prev => ({
                        ...prev,
                        shop_id: data.shop_id,
                        shop_name: data.shop_name || prev.shop_name,
                        shop_code: data.shop_code || prev.shop_code
                    }));
                }

                resetFilters();

                if (data.data.length === 0) {
                    showToast('info', translations.noUserRecords || 'No suppliers found in your shop');
                }
            } else {
                if (data.message.includes('not assigned to any shop')) {
                    showToast('error', translations.noShopPermission || 'You are not assigned to any shop. Please contact administrator.');
                } else {
                    showToast('error', data.message || translations.networkError || 'Network error occurred');
                }
            }
        } catch (error) {
            console.error('Error fetching suppliers:', error);
            showToast('error', translations.networkError || 'Network error occurred');
        } finally {
            setLoading(false);
        }
    };

    // Apply filters function
    const applyFilters = () => {
        let filtered = [...suppliers];

        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(supplier =>
                supplier.supplier_name?.toLowerCase().includes(term) ||
                supplier.phone?.toLowerCase().includes(term) ||
                supplier.email?.toLowerCase().includes(term) ||
                supplier.company?.toLowerCase().includes(term) ||
                supplier.contact_person?.toLowerCase().includes(term) ||
                supplier.address?.toLowerCase().includes(term) ||
                supplier.supplierid?.toString().includes(term)
            );
        }

        if (filterType === 'withPhone') {
            filtered = filtered.filter(supplier => supplier.phone && supplier.phone.trim());
        } else if (filterType === 'withoutPhone') {
            filtered = filtered.filter(supplier => !supplier.phone || !supplier.phone.trim());
        } else if (filterType === 'withEmail') {
            filtered = filtered.filter(supplier => supplier.email && supplier.email.trim());
        } else if (filterType === 'withoutEmail') {
            filtered = filtered.filter(supplier => !supplier.email || !supplier.email.trim());
        }

        if (filterBy === 'phone' && phoneFilter.trim()) {
            const phoneTerm = phoneFilter.toLowerCase();
            filtered = filtered.filter(supplier =>
                supplier.phone?.toLowerCase().includes(phoneTerm)
            );
        }

        if (filterBy === 'email' && emailFilter.trim()) {
            const emailTerm = emailFilter.toLowerCase();
            filtered = filtered.filter(supplier =>
                supplier.email?.toLowerCase().includes(emailTerm)
            );
        }

        if (filterBy === 'company' && companyFilter.trim()) {
            const companyTerm = companyFilter.toLowerCase();
            filtered = filtered.filter(supplier =>
                supplier.company?.toLowerCase().includes(companyTerm)
            );
        }

        if (filterBy === 'supplierId' && supplierIdFilter.trim()) {
            const idTerm = supplierIdFilter.toLowerCase();
            filtered = filtered.filter(supplier =>
                supplier.supplierid?.toString().toLowerCase().includes(idTerm)
            );
        }

        setFilteredSuppliers(filtered);
        setCurrentPage(1);
    };

    const resetFilters = () => {
        setFilterBy('all');
        setPhoneFilter('');
        setEmailFilter('');
        setCompanyFilter('');
        setSupplierIdFilter('');
        setSearchTerm('');
        setFilterType('all');
    };

    const handleSearch = () => {
        applyFilters();
    };

    const handleView = (supplier) => {
        setSelectedSupplier(supplier);
        setShowViewModal(true);
    };

    const handleEdit = (supplier) => {
        setSelectedSupplier(supplier);
        setEditFormData({
            supplier_name: supplier.supplier_name || '',
            phone: supplier.phone || '',
            email: supplier.email || '',
            company: supplier.company || '',
            address: supplier.address || '',
            website: supplier.website || '',
            contact_person: supplier.contact_person || '',
            notes: supplier.notes || ''
        });
        setEditFormErrors({});
        setShowEditModal(true);
    };

    const handleDeleteClick = (supplierId) => {
        const supplier = suppliers.find(s => s.supplierid === supplierId);
        if (!supplier) return;

        setSupplierToDelete(supplierId);
        setShowDeleteModal(true);
    };

    const handleDelete = async () => {
        if (!supplierToDelete || !currentShop) return;

        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/suppliers.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    action: 'delete',
                    supplierid: supplierToDelete,
                    username: user.username,
                    shop_id: currentShop.shop_id
                })
            });

            const data = await response.json();
            if (data.status === 'success') {
                showToast('success', translations.deletedSuccess || 'Supplier deleted successfully');
                fetchSuppliers();
                setShowDeleteModal(false);
                setSupplierToDelete(null);
            } else {
                showToast('error', data.message || translations.deleteError || 'Error deleting supplier');
            }
        } catch (error) {
            console.error('Error deleting supplier:', error);
            showToast('error', translations.deleteError || 'Error deleting supplier');
        }
    };

    // Validate edit form
    const validateEditForm = () => {
        const newErrors = {};

        if (!editFormData.supplier_name.trim()) {
            newErrors.supplier_name = translations.nameRequired || 'Supplier name is required';
        } else if (editFormData.supplier_name.trim().length < 2) {
            newErrors.supplier_name = translations.nameTooShort || 'Name must be at least 2 characters';
        }

        if (editFormData.phone && !/^[\d\s\-\+\(\)]{10,15}$/.test(editFormData.phone)) {
            newErrors.phone = translations.phoneInvalid || 'Please enter a valid phone number';
        }

        if (editFormData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(editFormData.email)) {
            newErrors.email = translations.emailInvalid || 'Please enter a valid email address';
        }

        if (editFormData.website && !/^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/.test(editFormData.website)) {
            newErrors.website = translations.websiteInvalid || 'Please enter a valid website URL';
        }

        setEditFormErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleEditFormChange = (e) => {
        const { name, value } = e.target;
        setEditFormData(prev => ({
            ...prev,
            [name]: value
        }));

        if (editFormErrors[name]) {
            setEditFormErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleUpdateSupplier = async (e) => {
        e.preventDefault();

        if (!selectedSupplier || !currentShop) return;

        if (!validateEditForm()) {
            return;
        }

        setEditLoading(true);

        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/suppliers.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    action: 'edit',
                    supplierid: selectedSupplier.supplierid,
                    ...editFormData,
                    username: user.username,
                    shop_id: currentShop.shop_id
                })
            });

            const data = await response.json();
            if (data.status === 'success') {
                showToast('success', translations.supplierUpdated || 'Supplier updated successfully');
                fetchSuppliers();
                setShowEditModal(false);
                setSelectedSupplier(null);
                setEditFormErrors({});
            } else {
                showToast('error', data.message || translations.updateError || 'Error updating supplier');
            }
        } catch (error) {
            console.error('Error updating supplier:', error);
            showToast('error', translations.updateError || 'Error updating supplier');
        } finally {
            setEditLoading(false);
        }
    };

    const handlePageChange = (page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    // Format date for PDF
    const formatDateGlobal = (dateString) => {
        if (!dateString) return '-';
        try {
            const date = new Date(dateString);
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        } catch (error) {
            return dateString;
        }
    };

    // Get current date for PDF
    const currentDateGlobal = () => {
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    // Export to PDF with options from row
    const handleExportPDF = async () => {
        if (filteredSuppliers.length === 0) {
            showToast('error', translations.noSuppliersFound || 'No data to export!');
            return;
        }

        setPdfLoading(true);

        try {
            const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';
            const primaryColor = currentTheme || '#6366f1';

            const pageSizeMap = {
                'a4': 'a4',
                'letter': 'letter',
                'legal': 'legal',
                'a5': 'a5'
            };

            const pageFormat = pageSizeMap[pdfPageSize] || 'a4';

            const pdf = new jsPDF({
                orientation: pdfOrientation,
                unit: 'mm',
                format: pageFormat,
                compress: true
            });

            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();

            const rtlTextStyles = isRTL ? `
                unicode-bidi: plaintext;
                text-rendering: optimizeLegibility;
                font-kerning: normal;
                font-variant-ligatures: common-ligatures;
                font-feature-settings: "calt", "liga", "kern";
                letter-spacing: 0;
                word-spacing: normal;
            ` : '';

            const headerCellStyle = `
                padding: 12px 10px; 
                border: none; 
                text-align: center; 
                font-weight: 600; 
                font-size: ${pdfOrientation === 'portrait' ? '11px' : '10px'}; 
                letter-spacing: ${isRTL ? '0' : '0.3px'};
                border-right: 1px solid rgba(255,255,255,0.1);
                ${rtlTextStyles}
            `;

            const addPatternBackground = (pdfDoc) => {
                pdfDoc.setFillColor(250, 251, 252);
                pdfDoc.rect(0, 0, pageWidth, pageHeight, 'F');

                const themeColor = hexToRgba(primaryColor, 0.5).match(/\d+/g);
                const r = parseInt(themeColor[0]);
                const g = parseInt(themeColor[1]);
                const b = parseInt(themeColor[2]);

                pdfDoc.setFillColor(r, g, b);
                pdfDoc.setDrawColor(r, g, b);
                pdfDoc.setLineWidth(0.2);

                const dotSize = 0.3;
                const dotSpacing = 8;

                for (let x = dotSpacing; x < pageWidth - dotSpacing; x += dotSpacing) {
                    for (let y = dotSpacing; y < pageHeight - dotSpacing; y += dotSpacing) {
                        pdfDoc.circle(x, y, dotSize, 'F');
                    }
                }

                pdfDoc.setFillColor(255, 255, 255);
                pdfDoc.setTextColor(0, 0, 0);
            };

            const rowsPerPage = getRowsPerPage();
            
            let suppliersToExport = [...filteredSuppliers];
            
            if (pdfPageRange === 'range') {
                const totalPages = Math.ceil(suppliersToExport.length / rowsPerPage);
                const start = Math.max(1, pdfStartPage);
                const end = Math.min(totalPages, pdfEndPage);
                const startIdx = (start - 1) * rowsPerPage;
                const endIdx = Math.min(end * rowsPerPage, suppliersToExport.length);
                suppliersToExport = suppliersToExport.slice(startIdx, endIdx);
            }
            
            const totalPages = Math.ceil(suppliersToExport.length / rowsPerPage);
            
            const headerHTML = `
                <div style="margin-bottom: 3px; padding-bottom: 5px; text-align: center; border-bottom: 2px solid ${hexToRgba(primaryColor, 0.05)}">
                    <div style="margin-bottom: 3px;">
                        <h1 style="color: ${primaryColor}; font-size: 22px; margin: 0 0 5px 0; font-weight: 800; letter-spacing: ${isRTL ? 'normal' : '-0.5px'}; ${rtlTextStyles}">
                            ${currentShop?.shop_name || translations.shopSuppliers || 'Shop Suppliers'}
                        </h1>
                        <div style="color: #666; font-size: 11px; font-weight: 500; margin-top: 3px; ${rtlTextStyles}">
                            ${businessInfo.businessname || user?.business_name || ''}
                        </div>
                        ${businessInfo.businessaddress ? `<div style="color: #888; font-size: 9px; margin-top: 2px; ${rtlTextStyles}">${businessInfo.businessaddress}</div>` : ''}
                        <div style="color: #888; font-size: 10px; margin-top: 4px; ${rtlTextStyles}">
                            ${currentShop?.shop_name ? `${translations.shopName || 'Shop'}: ${currentShop.shop_name} | ` : ''}
                            Total: ${suppliersToExport.length} suppliers
                        </div>
                        <div style="color: #888; font-size: 9px; margin-top: 2px; ${rtlTextStyles}">
                            Generated on ${currentDateGlobal()} by ${user?.fullname || user?.username || 'System'}
                        </div>
                    </div>
                </div>
            `;

            const filterInfoHTML = (searchTerm || filterType !== 'all' || phoneFilter || emailFilter || companyFilter) ? `
                <div style="margin: 8px 0; padding: 6px; background: #f9fafb; border-radius: 6px; font-size: 8px; color: #6b7280; text-align: center;">
                    ${translations.reportFilters || 'Applied Filters'}: 
                    ${searchTerm ? `Search: ${searchTerm}` : ''}
                    ${filterType !== 'all' ? ` | ${filterType}` : ''}
                    ${phoneFilter ? ` | Phone: ${phoneFilter}` : ''}
                    ${emailFilter ? ` | Email: ${emailFilter}` : ''}
                    ${companyFilter ? ` | Company: ${companyFilter}` : ''}
                </div>
            ` : '';

            const tableHeaderHTML = `
                <table style="width: 100%; border-collapse: separate; border-spacing: 0; margin-top: 5px; font-size: ${pdfOrientation === 'portrait' ? '9px' : '8px'}; border-radius: 5px; overflow: hidden; border: 1px solid ${hexToRgba(primaryColor, 0.15)};">
                    <thead>
                        <tr style="background: linear-gradient(135deg, ${primaryColor}, ${hexToRgba(primaryColor, 0.9)}); color: white;">
                            <th style="${headerCellStyle}">${translations.serialNo || 'No.'}</th>
                            <th style="${headerCellStyle}">${translations.supplierName || 'Supplier Name'}</th>
                            <th style="${headerCellStyle}">${translations.phone || 'Phone'}</th>
                            <th style="${headerCellStyle}">${translations.email || 'Email'}</th>
                            <th style="${headerCellStyle.replace('border-right:', 'border: none;')}">${translations.company || 'Company'}</th>
                        </tr>
                    </thead>
                    <tbody>
            `;

            for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
                const startRow = (pageNum - 1) * rowsPerPage;
                const endRow = Math.min(pageNum * rowsPerPage, suppliersToExport.length);
                const pageSuppliers = suppliersToExport.slice(startRow, endRow);

                const tableRowsHTML = pageSuppliers.map((supplier, index) => `
                    <tr style="${index % 2 === 0 ? 'background-color: #fafafa;' : 'background-color: #ffffff;'}">
                        <td style="padding: 6px; border: none; text-align: center; font-weight: 600; color: ${primaryColor}; border-bottom: 1px solid #f0f0f0;">
                            <div style="display: inline-flex; align-items: center; justify-content: center; width: 20px; height: 20px; background: ${hexToRgba(primaryColor, 0.1)}; border-radius: 3px; font-size: 9px;">
                                ${startRow + index + 1}
                            </div>
                        </td>
                        <td style="padding: 6px; border: none; text-align: center; font-weight: 500; color: #111827; border-bottom: 1px solid #f0f0f0; font-size: 9px; ${rtlTextStyles}">
                            <div style="font-weight: 600;">${supplier.supplier_name || '-'}</div>
                        </td>
                        <td style="padding: 6px; border: none; text-align: center; color: #4b5563; border-bottom: 1px solid #f0f0f0; font-size: 9px;">
                            ${supplier.phone || '-'}
                        </td>
                        <td style="padding: 6px; border: none; text-align: center; color: #4b5563; border-bottom: 1px solid #f0f0f0; font-size: 9px;">
                            ${supplier.email || '-'}
                        </td>
                        <td style="padding: 6px; border: none; text-align: center; color: #4b5563; border-bottom: 1px solid #f0f0f0; font-size: 9px;">
                            ${supplier.company || '-'}
                        </td>
                    </tr>
                `).join('');

                const tableHTML = tableHeaderHTML + tableRowsHTML + '</tbody></table>';
                
                const pageHTML = `
                    <div style="width: 800px; padding: 15px 25px; background-color: rgba(255, 255, 255, 0.98); font-family: ${isRTL ? "'Noto Naskh Arabic', 'Segoe UI', Tahoma" : "'Inter', 'Segoe UI', sans-serif"}; direction: ${isRTL ? 'rtl' : 'ltr'}; text-align: ${isRTL ? 'right' : 'left'}; color: #1a1a1a; border-radius: 4px; border: 1px solid ${hexToRgba(primaryColor, 0.1)};">
                        ${headerHTML}
                        ${filterInfoHTML}
                        <div style="overflow-x: auto;">
                            ${tableHTML}
                        </div>
                    </div>
                `;

                const pdfContainer = document.createElement('div');
                pdfContainer.style.position = 'absolute';
                pdfContainer.style.left = '-9999px';
                pdfContainer.style.top = '0';
                pdfContainer.style.width = '800px';
                pdfContainer.innerHTML = pageHTML;
                document.body.appendChild(pdfContainer);

                const canvas = await html2canvas(pdfContainer, {
                    scale: 1.5,
                    useCORS: true,
                    backgroundColor: '#ffffff',
                    logging: false,
                    onclone: (clonedDoc) => {
                        if (isRTL) {
                            const elements = clonedDoc.querySelectorAll('div, span, td, th, h1');
                            elements.forEach(el => {
                                el.style.unicodeBidi = 'plaintext';
                                el.style.textRendering = 'optimizeLegibility';
                                el.style.fontFeatureSettings = '"calt", "liga", "kern"';
                            });
                        }
                    }
                });

                document.body.removeChild(pdfContainer);

                const imgWidth = pageWidth - 20;
                const imgHeight = (canvas.height * imgWidth) / canvas.width;

                if (pageNum > 1) {
                    pdf.addPage();
                }

                addPatternBackground(pdf);
                pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 10, 10, imgWidth, Math.min(imgHeight, pageHeight - 20));

                pdf.setFontSize(8);
                pdf.setTextColor(120, 120, 120);
                pdf.text(`Page ${pageNum} of ${totalPages}`, pageWidth / 2, pageHeight - 8, { align: 'center' });
            }

            const pageSizeText = pdfPageSize === 'a4' ? 'A4' : pdfPageSize === 'letter' ? 'Letter' : pdfPageSize === 'legal' ? 'Legal' : 'A5';
            const orientationText = pdfOrientation === 'portrait' ? 'Portrait' : 'Landscape';
            const fileName = `suppliers_${currentShop?.shop_name?.replace(/[^a-z0-9]/gi, '_') || 'report'}_${pageSizeText}_${orientationText}_${currentDateGlobal()}.pdf`;
            
            pdf.save(fileName);
            
            showToast('success', `PDF exported successfully with ${suppliersToExport.length} supplier records across ${totalPages} pages!`);
        } catch (error) {
            console.error('Error generating PDF:', error);
            showToast('error', 'Error generating PDF. Please try again.');
        } finally {
            setPdfLoading(false);
        }
    };

    const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';

    return (
        <div className={`customer-list-modern ${isRTL ? 'rtl' : ''}`}>
            {/* Toast Notification */}
            <ToastNotification
                toast={toast}
                setToast={setToast}
                translations={translations}
            />

            {/* Shop Selector Modal */}
            <ShopSelectorModal
                isOpen={showShopSelector}
                onClose={() => setShowShopSelector(false)}
                shops={availableShops}
                currentShop={currentShop}
                onSelectShop={handleSwitchShop}
                translations={translations}
                loading={loadingShops}
            />

            {/* Header Section */}
            <div className="list-header-modern">
                <div className="header-content">
                    <div className="header-icon">
                        <FontAwesomeIcon icon={faTruck} />
                    </div>
                    <div className="header-text">
                        <h1>{translations.supplierList || 'Supplier List'}</h1>
                        <div className="shop-info">
                            <p>
                                {currentShop?.shop_name
                                    ? `${translations.shopName || 'Shop'}: ${currentShop.shop_name}`
                                    : `${translations.allSuppliers || 'My Suppliers'} - ${user?.username}`}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Shop Indicator */}
                {renderShopIndicator()}
            </div>

            {/* Advanced Filters Section with PDF Options in same row */}
            <div className="advanced-filters-section">
                <div className="filters-row">
                    <div className="filter-group compact">
                        <select
                            value={filterType}
                            onChange={(e) => setFilterType(e.target.value)}
                            className="filter-select"
                            dir={isRTL ? 'rtl' : 'ltr'}
                        >
                            <option value="all">{translations.all || 'All'}</option>
                            <option value="withPhone">{translations.withPhone || 'With Phone'}</option>
                            <option value="withoutPhone">{translations.withoutPhone || 'Without Phone'}</option>
                            <option value="withEmail">{translations.withEmail || 'With Email'}</option>
                            <option value="withoutEmail">{translations.withoutEmail || 'Without Email'}</option>
                        </select>
                    </div>

                    <div className="filter-group compact">
                        <select
                            value={filterBy}
                            onChange={(e) => setFilterBy(e.target.value)}
                            className="filter-select"
                            dir={isRTL ? 'rtl' : 'ltr'}
                        >
                            <option value="all">{translations.filterBy || 'Filter By'}</option>
                            <option value="phone">{translations.phoneNumber || 'Phone Number'}</option>
                            <option value="email">{translations.emailAddress || 'Email Address'}</option>
                            <option value="company">{translations.companyName || 'Company Name'}</option>
                            <option value="supplierId">{translations.supplierIdFilter || 'Supplier ID'}</option>
                        </select>
                    </div>

                    {filterBy === 'phone' && (
                        <div className="filter-group compact">
                            <input
                                type="text"
                                value={phoneFilter}
                                onChange={(e) => setPhoneFilter(e.target.value)}
                                placeholder={translations.phoneNumber || 'Phone number...'}
                                className="filter-input compact"
                                dir={isRTL ? 'rtl' : 'ltr'}
                            />
                        </div>
                    )}

                    {filterBy === 'email' && (
                        <div className="filter-group compact">
                            <input
                                type="email"
                                value={emailFilter}
                                onChange={(e) => setEmailFilter(e.target.value)}
                                placeholder={translations.emailAddress || 'Email address...'}
                                className="filter-input compact"
                                dir="ltr"
                            />
                        </div>
                    )}

                    {filterBy === 'company' && (
                        <div className="filter-group compact">
                            <input
                                type="text"
                                value={companyFilter}
                                onChange={(e) => setCompanyFilter(e.target.value)}
                                placeholder={translations.companyName || 'Company name...'}
                                className="filter-input compact"
                                dir={isRTL ? 'rtl' : 'ltr'}
                            />
                        </div>
                    )}

                    {filterBy === 'supplierId' && (
                        <div className="filter-group compact">
                            <input
                                type="text"
                                value={supplierIdFilter}
                                onChange={(e) => setSupplierIdFilter(e.target.value)}
                                placeholder={translations.supplierIdFilter || 'Supplier ID...'}
                                className="filter-input compact"
                                dir={isRTL ? 'rtl' : 'ltr'}
                            />
                        </div>
                    )}

                    <div className="filter-group compact search-group">
                        <div className="search-box compact">
                            <input
                                type="text"
                                placeholder={translations.searchPlaceholder || 'Search ...'}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                                dir={isRTL ? 'rtl' : 'ltr'}
                            />
                        </div>
                    </div>

                    <div className="filter-group compact">
                        <button
                            onClick={fetchSuppliers}
                            className="refresh-btn-modern compact"
                            disabled={loading}
                            title={translations.refresh || 'Refresh'}
                        >
                            <FontAwesomeIcon icon={faRefresh} />
                            <span className="btn-text">{translations.refresh || 'Refresh'}</span>
                        </button>
                    </div>

                    <div className="filter-group compact">
                        <select 
                            value={pdfOrientation} 
                            onChange={(e) => setPdfOrientation(e.target.value)} 
                            className="filter-select compact"
                            title={translations.orientation || 'Orientation'}
                            style={{ minWidth: '100px' }}
                        >
                            <option value="portrait">{translations.portrait || 'Portrait'}</option>
                            <option value="landscape">{translations.landscape || 'Landscape'}</option>
                        </select>
                    </div>

                    <div className="filter-group compact">
                        <select 
                            value={pdfPageSize} 
                            onChange={(e) => setPdfPageSize(e.target.value)} 
                            className="filter-select compact"
                            title={translations.pageSize || 'Page Size'}
                            style={{ minWidth: '90px' }}
                        >
                            <option value="a4">{translations.a4 || 'A4'}</option>
                            <option value="letter">{translations.letter || 'Letter'}</option>
                            <option value="legal">{translations.legal || 'Legal'}</option>
                            <option value="a5">{translations.a5 || 'A5'}</option>
                        </select>
                    </div>

                    <div className="filter-group compact">
                        <select 
                            value={pdfPageRange} 
                            onChange={(e) => setPdfPageRange(e.target.value)} 
                            className="filter-select compact"
                            title="Page Range"
                            style={{ minWidth: '110px' }}
                        >
                            <option value="all">All Pages</option>
                            <option value="range">Page Range</option>
                        </select>
                    </div>

                    {pdfPageRange === 'range' && (
                        <>
                            <div className="filter-group compact" style={{ width: '70px' }}>
                                <input 
                                    type="number" 
                                    min="1" 
                                    value={pdfStartPage} 
                                    onChange={(e) => setPdfStartPage(Math.max(1, parseInt(e.target.value) || 1))}
                                    className="filter-input compact"
                                    placeholder="From"
                                    style={{ textAlign: 'center' }}
                                />
                            </div>
                            <div className="filter-group compact" style={{ width: '70px' }}>
                                <input 
                                    type="number" 
                                    min={pdfStartPage} 
                                    value={pdfEndPage} 
                                    onChange={(e) => setPdfEndPage(Math.max(pdfStartPage, parseInt(e.target.value) || pdfStartPage))}
                                    className="filter-input compact"
                                    placeholder="To"
                                    style={{ textAlign: 'center' }}
                                />
                            </div>
                        </>
                    )}

                    <div className="filter-group compact">
                        <button
                            onClick={handleExportPDF}
                            className="refresh-btn-modern compact"
                            disabled={loading || filteredSuppliers.length === 0 || pdfLoading}
                            title={translations.exportToPDF || 'Export PDF'}
                            style={{ background: 'linear-gradient(135deg, #dc2626, #b91c1c)' }}
                        >
                            {pdfLoading ? (
                                <div className="btn-spinner-small"></div>
                            ) : (
                                <FontAwesomeIcon icon={faFilePdf} />
                            )}
                            <span className="btn-text">{translations.exportToPDF || 'Export PDF'}</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* Loading State */}
            {loading ? (
                <div className="loading-container">
                    <div className="loading-spinner-modern"></div>
                    <p>{translations.loadingUserData || 'Loading shop supplier data...'}</p>
                </div>
            ) : !currentShop ? (
                <div className="no-shop-container">
                    <div className="no-shop-message">
                        <FontAwesomeIcon icon={faStore} />
                        <h3>{translations.noShopSelected || 'No Shop Selected'}</h3>
                        <p>{translations.selectShop || 'Please select a shop to view suppliers'}</p>
                        <button
                            className="btn-modern btn-primary-modern"
                            onClick={() => setShowShopSelector(true)}
                        >
                            <FontAwesomeIcon icon={faExchangeAlt} />
                            <span>{translations.switchShop || 'Switch Shop'}</span>
                        </button>
                    </div>
                </div>
            ) : (
                <>
                    {/* Table Section */}
                    <div className="table-scroll-container">
                        <div className="table-container-modern">
                            <div className="customer-table-wrapper">
                                <table className="customer-table-modern">
                                    <thead>
                                        <tr>
                                            <th className="id-column centered-cell">{translations.serialNo || 'S.No'}</th>
                                            <th className="name-column centered-cell">{translations.supplierName || 'Supplier Name'}</th>
                                            <th className="phone-column centered-cell">{translations.phone || 'Phone'}</th>
                                            <th className="email-column centered-cell">{translations.email || 'Email'}</th>
                                            <th className="company-column centered-cell">{translations.company || 'Company'}</th>
                                            <th className="actions-column centered-cell">{translations.actions || 'Actions'}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentSuppliers.map((supplier, index) => (
                                            <tr key={supplier.supplierid}>
                                                <td className="id-cell centered-cell">
                                                    <div className="cell-content centered-content">
                                                        <span className="cell-text">{startIndex + index + 1}</span>
                                                    </div>
                                                </td>
                                                <td className="name-cell centered-cell">
                                                    <div className="cell-content centered-content">
                                                        <span className="cell-text">{supplier.supplier_name}</span>
                                                    </div>
                                                </td>
                                                <td className="phone-cell centered-cell">
                                                    <div className="cell-content centered-content">
                                                        {supplier.phone ? (
                                                            <span className="cell-text">{supplier.phone}</span>
                                                        ) : '-'}
                                                    </div>
                                                </td>
                                                <td className="email-cell centered-cell">
                                                    <div className="cell-content centered-content">
                                                        {supplier.email ? (
                                                            <span className="cell-text">{supplier.email}</span>
                                                        ) : '-'}
                                                    </div>
                                                </td>
                                                <td className="company-cell centered-cell">
                                                    <div className="cell-content centered-content">
                                                        {supplier.company ? (
                                                            <span className="cell-text">{supplier.company}</span>
                                                        ) : '-'}
                                                    </div>
                                                </td>
                                                <td className="actions-cell-modern centered-cell">
                                                    <div className="cell-content centered-content actions-content">
                                                        <button
                                                            onClick={() => handleEdit(supplier)}
                                                            className="action-btn-modern edit-btn-modern"
                                                            title={translations.edit || 'Edit'}
                                                        >
                                                            <FontAwesomeIcon icon={faEdit} />
                                                        </button>
                                                        <button
                                                            onClick={() => handleDeleteClick(supplier.supplierid)}
                                                            className="action-btn-modern delete-btn-modern"
                                                            title={translations.delete || 'Delete'}
                                                        >
                                                            <FontAwesomeIcon icon={faTrash} />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    {/* Pagination */}
                    {filteredSuppliers.length > 0 && (
                        <div className="pagination-modern">
                            <div className="pagination-info">
                                <span>{translations.showing || 'Showing'} {startIndex + 1}-{Math.min(endIndex, filteredSuppliers.length)} {translations.of || 'of'} {filteredSuppliers.length} {translations.suppliers || 'suppliers'}</span>
                                {currentShop?.shop_name && (
                                    <span className="shop-info-pagination">
                                        | {translations.shopName || 'Shop'}: {currentShop.shop_name}
                                    </span>
                                )}
                            </div>
                            <div className="pagination-controls">
                                <button
                                    onClick={() => handlePageChange(currentPage - 1)}
                                    disabled={currentPage === 1}
                                    className="pagination-btn"
                                >
                                    <FontAwesomeIcon icon={faChevronLeft} />
                                    <span>{translations.previous || 'Previous'}</span>
                                </button>

                                <div className="page-numbers">
                                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                        let pageNum;
                                        if (totalPages <= 5) {
                                            pageNum = i + 1;
                                        } else if (currentPage <= 3) {
                                            pageNum = i + 1;
                                        } else if (currentPage >= totalPages - 2) {
                                            pageNum = totalPages - 4 + i;
                                        } else {
                                            pageNum = currentPage - 2 + i;
                                        }

                                        return (
                                            <button
                                                key={pageNum}
                                                onClick={() => handlePageChange(pageNum)}
                                                className={`page-number ${currentPage === pageNum ? 'active' : ''}`}
                                            >
                                                {pageNum}
                                            </button>
                                        );
                                    })}
                                </div>

                                <button
                                    onClick={() => handlePageChange(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                    className="pagination-btn"
                                >
                                    <span>{translations.next || 'Next'}</span>
                                    <FontAwesomeIcon icon={faChevronRight} />
                                </button>
                            </div>
                        </div>
                    )}
                </>
            )}

            {/* View Modal - Does NOT close on outside click */}
            {showViewModal && selectedSupplier && (
                <div className="modal-overlay-modern" onClick={(e) => e.stopPropagation()}>
                    <div className="modal-content-modern view-modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header-modern">
                            <div className="modal-icon">
                                <FontAwesomeIcon icon={faTruck} />
                            </div>
                            <div className="modal-title">
                                <h3>{translations.supplierDetails || 'Supplier Details'}</h3>
                                <span className="supplier-number-badge">#{selectedSupplier.supplierid}</span>
                            </div>
                            <button
                                className="close-btn-modern"
                                onClick={() => setShowViewModal(false)}
                            >
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>
                        <div className="modal-body-modern">
                            <div className="view-section">
                                <h4 className="view-section-title"><FontAwesomeIcon icon={faTruck} />Supplier Summary</h4>
                                <div className="view-grid">
                                    <div className="view-row"><span className="view-label">{translations.supplierId || 'Supplier ID'}:</span><span className="view-value">#{selectedSupplier.supplierid}</span></div>
                                    <div className="view-row"><span className="view-label">{translations.supplierName || 'Supplier Name'}:</span><span className="view-value">{selectedSupplier.supplier_name}</span></div>
                                    <div className="view-row"><span className="view-label">{translations.company || 'Company'}:</span><span className="view-value">{selectedSupplier.company || translations.notProvided || 'Not provided'}</span></div>
                                </div>
                            </div>
                            <div className="view-section">
                                <h4 className="view-section-title"><FontAwesomeIcon icon={faPhone} />{translations.contactInfo || 'Contact Information'}</h4>
                                <div className="view-grid">
                                    <div className="view-row"><span className="view-label">{translations.phone || 'Phone'}:</span><span className="view-value">{selectedSupplier.phone || translations.notProvided || 'Not provided'}</span></div>
                                    <div className="view-row"><span className="view-label">{translations.email || 'Email'}:</span><span className="view-value">{selectedSupplier.email || translations.notProvided || 'Not provided'}</span></div>
                                    <div className="view-row"><span className="view-label">{translations.website || 'Website'}:</span><span className="view-value">{selectedSupplier.website ? <a href={selectedSupplier.website} target="_blank" rel="noopener noreferrer">{selectedSupplier.website} <FontAwesomeIcon icon={faExternalLinkAlt} /></a> : translations.notProvided || 'Not provided'}</span></div>
                                    <div className="view-row"><span className="view-label">{translations.contactPerson || 'Contact Person'}:</span><span className="view-value">{selectedSupplier.contact_person || translations.notProvided || 'Not provided'}</span></div>
                                </div>
                            </div>
                            {selectedSupplier.address && (
                                <div className="view-section">
                                    <h4 className="view-section-title"><FontAwesomeIcon icon={faMapMarkerAlt} />{translations.addressInfo || 'Address Information'}</h4>
                                    <div className="remarks-box">{selectedSupplier.address}</div>
                                </div>
                            )}
                            {selectedSupplier.notes && (
                                <div className="view-section">
                                    <h4 className="view-section-title"><FontAwesomeIcon icon={faStickyNote} />{translations.notes || 'Notes'}</h4>
                                    <div className="remarks-box">{selectedSupplier.notes}</div>
                                </div>
                            )}
                            <div className="view-section metadata-section">
                                <div className="view-grid">
                                    <div className="view-row"><span className="view-label">{translations.shopName || 'Shop'}:</span><span className="view-value">{currentShop?.shop_name || 'N/A'}</span></div>
                                    {selectedSupplier.created_at && (
                                        <div className="view-row"><span className="view-label">{translations.dateAdded || 'Date Added'}:</span><span className="view-value">{formatDateGlobal(selectedSupplier.created_at)}</span></div>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer-modern">
                            <button className="btn-modern btn-secondary-modern" onClick={() => setShowViewModal(false)}>{translations.close || 'Close'}</button>
                            <button className="btn-modern btn-primary-modern" onClick={() => { setShowViewModal(false); handleEdit(selectedSupplier); }}>
                                <FontAwesomeIcon icon={faEdit} />
                                {translations.edit || 'Edit'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Modal - MOVABLE, RESIZABLE, Does NOT close on outside click */}
            {showEditModal && selectedSupplier && (
                <div className="modal-overlay-modern" onClick={(e) => e.stopPropagation()}>
                    <div 
                        ref={modalRef}
                        className="modal-content-modern edit-modal-content" 
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            position: 'fixed',
                            left: modalPosition.x || '50%',
                            top: modalPosition.y || '50%',
                            transform: modalPosition.x === 0 && modalPosition.y === 0 ? 'translate(-50%, -50%)' : 'none',
                            width: modalSize.width,
                            minWidth: '350px',
                            maxWidth: '95vw',
                            maxHeight: '85vh'
                        }}
                    >
                        {/* Resize Handles */}
                        <div className="resize-handle-top" onMouseDown={(e) => startResize(e, 'n')}></div>
                        <div className="resize-handle-bottom" onMouseDown={(e) => startResize(e, 's')}></div>
                        <div className="resize-handle-left" onMouseDown={(e) => startResize(e, 'w')}></div>
                        <div className="resize-handle-right" onMouseDown={(e) => startResize(e, 'e')}></div>
                        <div className="resize-handle-nw" onMouseDown={(e) => startResize(e, 'nw')}></div>
                        <div className="resize-handle-ne" onMouseDown={(e) => startResize(e, 'ne')}></div>
                        <div className="resize-handle-sw" onMouseDown={(e) => startResize(e, 'sw')}></div>
                        <div className="resize-handle-se" onMouseDown={(e) => startResize(e, 'se')}></div>

                        {/* Draggable Header */}
                        <div className="modal-header-modern" onMouseDown={startDrag}>
                            <div className="modal-icon">
                                <FontAwesomeIcon icon={faUserEdit} />
                            </div>
                            <div className="modal-title">
                                <h3>{translations.editSupplier || 'Edit Supplier'}</h3>
                                <span className="supplier-number-badge">#{selectedSupplier.supplierid}</span>
                            </div>
                            
                            {/* Width Control Buttons */}
                            <div className="width-control-group">
                                {Object.entries(widthModes).map(([key, mode]) => (
                                    <button
                                        key={key}
                                        className={`width-btn ${currentWidthMode === key ? 'active' : ''}`}
                                        onClick={() => setWidthMode(key)}
                                        title={mode.label}
                                    >
                                        <FontAwesomeIcon icon={mode.icon} />
                                    </button>
                                ))}
                            </div>
                            
                            <button className="close-btn-modern" onClick={() => setShowEditModal(false)}>
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>

                        <form onSubmit={handleUpdateSupplier}>
                            <div className="modal-body-modern">
                                <div className="form-grid">
                                    <div className={`form-group ${editFormErrors.supplier_name ? 'has-error' : ''}`}>
                                        <label htmlFor="supplier_name">{translations.supplierName || 'Supplier Name'} <span className="required">*</span></label>
                                        <input
                                            type="text"
                                            id="supplier_name"
                                            name="supplier_name"
                                            value={editFormData.supplier_name}
                                            onChange={handleEditFormChange}
                                            className={editFormErrors.supplier_name ? 'error' : ''}
                                            dir={isRTL ? 'rtl' : 'ltr'}
                                        />
                                        {editFormErrors.supplier_name && (<div className="error-message"><FontAwesomeIcon icon={faExclamationTriangle} />{editFormErrors.supplier_name}</div>)}
                                    </div>

                                    <div className={`form-group ${editFormErrors.phone ? 'has-error' : ''}`}>
                                        <label htmlFor="phone">{translations.phone || 'Phone'}</label>
                                        <input
                                            type="tel"
                                            id="phone"
                                            name="phone"
                                            value={editFormData.phone}
                                            onChange={handleEditFormChange}
                                            dir="ltr"
                                        />
                                        {editFormErrors.phone && (<div className="error-message"><FontAwesomeIcon icon={faExclamationTriangle} />{editFormErrors.phone}</div>)}
                                    </div>

                                    <div className={`form-group ${editFormErrors.email ? 'has-error' : ''}`}>
                                        <label htmlFor="email">{translations.email || 'Email'}</label>
                                        <input
                                            type="email"
                                            id="email"
                                            name="email"
                                            value={editFormData.email}
                                            onChange={handleEditFormChange}
                                            dir="ltr"
                                        />
                                        {editFormErrors.email && (<div className="error-message"><FontAwesomeIcon icon={faExclamationTriangle} />{editFormErrors.email}</div>)}
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="company">{translations.company || 'Company'}</label>
                                        <input
                                            type="text"
                                            id="company"
                                            name="company"
                                            value={editFormData.company}
                                            onChange={handleEditFormChange}
                                            dir={isRTL ? 'rtl' : 'ltr'}
                                        />
                                    </div>

                                    <div className={`form-group ${editFormErrors.website ? 'has-error' : ''}`}>
                                        <label htmlFor="website">{translations.website || 'Website'}</label>
                                        <input
                                            type="url"
                                            id="website"
                                            name="website"
                                            value={editFormData.website}
                                            onChange={handleEditFormChange}
                                            dir="ltr"
                                        />
                                        {editFormErrors.website && (<div className="error-message"><FontAwesomeIcon icon={faExclamationTriangle} />{editFormErrors.website}</div>)}
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="contact_person">{translations.contactPerson || 'Contact Person'}</label>
                                        <input
                                            type="text"
                                            id="contact_person"
                                            name="contact_person"
                                            value={editFormData.contact_person}
                                            onChange={handleEditFormChange}
                                            dir={isRTL ? 'rtl' : 'ltr'}
                                        />
                                    </div>

                                    <div className="form-group full-width">
                                        <label htmlFor="address">{translations.address || 'Address'}</label>
                                        <textarea
                                            id="address"
                                            name="address"
                                            value={editFormData.address}
                                            onChange={handleEditFormChange}
                                            rows="3"
                                            dir={isRTL ? 'rtl' : 'ltr'}
                                        />
                                    </div>

                                    <div className="form-group full-width">
                                        <label htmlFor="notes">{translations.notes || 'Notes'}</label>
                                        <textarea
                                            id="notes"
                                            name="notes"
                                            value={editFormData.notes}
                                            onChange={handleEditFormChange}
                                            rows="3"
                                            dir={isRTL ? 'rtl' : 'ltr'}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer-modern">
                                <button type="button" className="btn-modern btn-secondary-modern" onClick={() => setShowEditModal(false)}>
                                    {translations.cancel || 'Cancel'}
                                </button>
                                <button type="submit" className="btn-modern btn-primary-modern" disabled={editLoading}>
                                    {editLoading ? (
                                        <><div className="btn-spinner"></div><span>{translations.updating || 'Updating...'}</span></>
                                    ) : (
                                        <><FontAwesomeIcon icon={faSave} /><span>{translations.saveChanges || 'Save Changes'}</span></>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal - Does NOT close on outside click */}
            {showDeleteModal && (
                <div className="modal-overlay-modern" onClick={(e) => e.stopPropagation()}>
                    <div className="modal-content-modern delete-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header-modern delete-header">
                            <div className="modal-icon">
                                <FontAwesomeIcon icon={faExclamationTriangle} />
                            </div>
                            <div className="modal-title">
                                <h3 style={{ color: 'var(--customer-error-color)' }}>
                                    {translations.confirmDelete || 'Confirm Delete'}
                                </h3>
                            </div>
                            <button
                                className="close-btn-modern"
                                onClick={() => setShowDeleteModal(false)}
                            >
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>
                        <div className="modal-body-modern">
                            <div className="delete-message">
                                <p style={{ fontSize: '1.1rem', marginBottom: '10px', color: 'var(--customer-text-primary)' }}>
                                    {translations.deleteMessage || 'Are you sure you want to delete this supplier from your shop?'}
                                </p>
                                <p style={{ fontSize: '0.9rem', color: 'var(--customer-warning-color)' }}>
                                    <FontAwesomeIcon icon={faExclamationTriangle} />{' '}
                                    {translations.deleteWarning || 'This action cannot be undone.'}
                                </p>
                            </div>
                        </div>
                        <div className="modal-footer-modern">
                            <button
                                type="button"
                                className="btn-modern btn-secondary-modern"
                                onClick={() => setShowDeleteModal(false)}
                            >
                                <FontAwesomeIcon icon={faTimes} />
                                {translations.noCancel || 'No, Cancel'}
                            </button>
                            <button
                                type="button"
                                className="btn-modern btn-delete-modern"
                                onClick={handleDelete}
                            >
                                <FontAwesomeIcon icon={faTrash} />
                                {translations.yesDelete || 'Yes, Delete'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SupplierList;